import * as joint from "@clientio/rappid";
import { State, StateView } from "../../mvc/state";
import { Transition, TransitionView } from "../../mvc/transition";
import { BaseNode } from "../../mvc/base/base-node";
import { Region } from "../../mvc/region";
import { Entry } from "../../mvc/entry";
import RappidContext from "../../rappid/rappid-context";
import { DirectedGraph } from "@joint/layout-directed-graph";
import { dia } from "@joint/core";
import { LabeledNode } from "../../mvc/base/labeled-node";
import { PreferenceStore } from "../preference-store";
import { getConfig } from "../../../../config";
// If the config file does not exist, create it at src/config.ts and export getConfig.
import { FallbackGraphLayouter } from "./fallback-layouter";

export class GraphLayouter {
  private fallbackLayouter = new FallbackGraphLayouter();

  layout(context: RappidContext) {
    const { layoutEnabled } = getConfig();
    if (!layoutEnabled) {
      // Fallback layout controlled by toggle switch
      return this.fallbackLayouter.layout(context);
    }

    try {
      // disable dynamic routing while laying out the graph
      context.routerController.removeGraphListeners();
      context.commandManager.initBatchCommand();
      const graph: dia.Graph = context.graph;
      const preprocessedLabeledNodes = this.preprocessLabeledNodes(graph);
      context.graph.getCells().forEach((cell) => {
        const view = cell.findView(context.paper);
        if (view instanceof StateView) {
          if (!view.aceEditor) {
            view.update();
          }
          view.fitToContent();
        }
        if (view instanceof TransitionView) {
          view.update();
        }
      });
      const topLevelElements = context.graph.getElements().filter((el) => {
        return !el.getParentCell();
      });
      this.layoutNodesOnSameLevel(null, topLevelElements, context, 0);
      preprocessedLabeledNodes.forEach(({ cell, children }) => {
        children.forEach(({ child, dx, dy }) => {
          child.position(dx, dy, { parentRelative: true });
        });
      });
      // enable routing and trigger it
      context.routerController.routeAll();
      context.commandManager.storeBatchCommand();
    } catch (error) {
      context.commandManager.storeBatchCommand();
      context.commandManager.undo();
    } finally {
      context.routerController.addGraphListeners();
      setTimeout(() => {
        context.paperScroller.zoom(1 - context.paperScroller.zoom());
        context.paperScroller.centerContent();
      });
    }
  }

  /* For some reason, the order of the elements is considered by DirectedGraph => Entries shall go first. */
  private preSortElements(elements: dia.Element<dia.Element.Attributes, dia.ModelSetOptions>[]) {
    return elements.sort((a, b) => {
      const aIsEntry = a instanceof Entry;
      const bIsEntry = b instanceof Entry;
      const aIsSync = a.get("type") === "synchronization";
      const bIsSync = b.get("type") === "synchronization";

      // Entries first
      if (aIsEntry && !bIsEntry) return -1;
      if (!aIsEntry && bIsEntry) return 1;

      // Synchronizations last
      if (aIsSync && !bIsSync) return 1;
      if (!aIsSync && bIsSync) return -1;

      return 0;
    });
  }

  private layoutNodesOnSameLevel(
    parent: dia.Cell,
    elementsOnSameLevel: dia.Element<dia.Element.Attributes, dia.ModelSetOptions>[],
    context: RappidContext,
    level: number
  ) {
    if (elementsOnSameLevel.length === 0) return;
    elementsOnSameLevel = this.preSortElements(elementsOnSameLevel);
    if (elementsOnSameLevel.length > 1) level++;

    const parentIsState = parent instanceof State;
    if (parentIsState && elementsOnSameLevel.length > 1) {
      const ld = parent.getLayoutDirection() || "horizontal";
      // for horizontal|vertical regions, we want TB|LR in each region
      if (("horizontal" === ld && level % 2 === 1) || ("horizontal" !== ld && level % 2 === 0)) {
        level++;
      }
    }

    if (!(parent instanceof LabeledNode)) {
      // No recursion to layout the Labels
      elementsOnSameLevel.forEach((el) => {
        this.layoutNodesOnSameLevel(
          el,
          el.getEmbeddedCells().filter((c) => c.isElement()) as dia.Element[],
          context,
          level
        );
      });
    }

    if (!(parent instanceof State)) {
      // No Layout for State's embedded regions, they just shall go side by side (handled by State's updateRegionSizes & fitToChildren )

      const beforePositions = new Map<dia.Cell.ID, { x: number; y: number }>();
      elementsOnSameLevel.forEach((el) => {
        beforePositions.set(el.id, el.position());
      });
      const elementIDs = new Set(elementsOnSameLevel.map((el) => el.id));
      const linksOnSameLevel: dia.Link[] = [];

      context.graph.getLinks().forEach((link) => {
        const sourceId = link.get("source")?.id;
        const targetId = link.get("target")?.id;
        const sourceOnLevel = elementIDs.has(sourceId);
        const targetOnLevel = elementIDs.has(targetId);

        if (sourceOnLevel && targetOnLevel) {
          linksOnSameLevel.push(link);
        }
      });

      const rankDir = level % 2 === 0 ? "LR" : "TB";
      const isTB = rankDir === "TB";
      const vNodeDist = PreferenceStore.INSTANCE.verticalNodeDistance;
      const hNodeDist = PreferenceStore.INSTANCE.horizontalNodeDistance;
      DirectedGraph.layout([...elementsOnSameLevel, ...linksOnSameLevel], {
        rankDir: rankDir,
        marginX: StateView.Padding,
        marginY: StateView.Padding,
        nodeSep: isTB ? hNodeDist : vNodeDist,
        edgeSep: 5,
        rankSep: isTB ? vNodeDist : hNodeDist,
        clusterPadding: { top: 1, left: 1, right: 1, bottom: 1 },
        setLinkVertices: false,
        setLabels: false,
        ranker: "network-simplex",
        align: "UL",
        resizeClusters: false,
      });

      const px = parent ? parent.position().x : 0;
      const py = parent ? parent.position().y : 0;

      /** These translations are a performance leak
       * I think we can get rid of it once the px py mystery is solved.
       */
      elementsOnSameLevel.forEach((el) => {
        if (el.getEmbeddedCells().length === 0 && px === 0 && py === 0) {
          return;
        }
        const before = beforePositions.get(el.id);
        const after = el.position();
        const dx = after.x - before.x;
        const dy = after.y - before.y;
        // Move el back to old position
        el.position(before.x, before.y);
        // Then move it and its embedded elements together
        el.translate(dx + px, dy + py);
      });
    }

    if (parent instanceof Region) {
      const grandParent = parent.getParentCell();
      if (!(grandParent instanceof State)) {
        parent.fitToChildren({ deep: false });
      }
    }

    if (parent instanceof State) {
      const view = parent.findView(context.paper) as StateView;
      view.fitToContent();
    }
  }

  private preprocessLabeledNodes(graph: dia.Graph): {
    cell: LabeledNode;
    children: { child: dia.Element; dx: number; dy: number }[];
  }[] {
    const preprocessedLabeledNodes: {
      cell: LabeledNode;
      children: { child: dia.Element; dx: number; dy: number }[];
    }[] = [];
    graph.getElements().forEach((cell) => {
      if (cell instanceof LabeledNode) {
        const parentPos = cell.position();
        const childrenWithOffset = cell
          .getEmbeddedCells()
          .filter((c): c is dia.Element => c.isElement())
          .map((child) => {
            const childPos = child.position();
            return {
              child,
              dx: childPos.x - parentPos.x,
              dy: childPos.y - parentPos.y,
            };
          });
        preprocessedLabeledNodes.push({
          cell,
          children: childrenWithOffset,
        });
      }
    });

    return preprocessedLabeledNodes;
  }
}
